import { useState } from "react";
import PageHeader from "@/components/layout/PageHeader";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { submitFinancing } from "@/lib/api";
import { Wallet, Clock, Layers, Building2 } from "lucide-react";

export default function FinancingPage() {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    estimated_budget: "",
    project_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email) {
      toast.error("Name, phone and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitFinancing(form);
      setDone(true);
      toast.success("Inquiry received. A specialist will reach out soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="financing-page">
      <PageHeader
        overline="Financing"
        title="Flexible financing options for qualified projects."
        sub="Construction is an investment. Financing options may be available for qualified customers — across HVAC, plumbing, remodels and ADU builds."
        image="https://images.pexels.com/photos/8482551/pexels-photo-8482551.jpeg"
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Financing" }]}
      />

      <section className="section-pad">
        <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="overline mb-3">Why financing</p>
            <h2 className="heading-2">Spread the cost of comfort, value and durability.</h2>
            <ul className="mt-8 space-y-4">
              {[
                { icon: Clock, title: "Fast Approval Process", text: "Quick turnaround — most decisions come back the same day." },
                { icon: Wallet, title: "Flexible Payment Options", text: "Choose terms that match your monthly budget." },
                { icon: Layers, title: "Across All Services", text: "HVAC, plumbing, remodels and full ADU builds — all eligible." },
                { icon: Building2, title: "Residential & Commercial", text: "Coverage for homeowners and small businesses alike." },
              ].map((it, i) => {
                const Icon = it.icon;
                return (
                  <li key={i} className="flex gap-4" data-testid={`financing-benefit-${i}`}>
                    <div className="w-10 h-10 rounded-sm bg-brand-limestone flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-brand-obsidian" strokeWidth={1.75} />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-brand-ink">{it.title}</p>
                      <p className="body-sm">{it.text}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="md:col-span-7">
            <div className="card-base p-7 md:p-9">
              {done ? (
                <div className="flex items-center gap-4 py-6" data-testid="financing-success">
                  <div className="w-12 h-12 rounded-full bg-brand-obsidian flex items-center justify-center shrink-0">
                    <Check className="w-6 h-6 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="font-heading text-xl font-bold text-brand-ink">Inquiry received.</p>
                    <p className="body-sm">A specialist will reach out within one business day.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="financing-form">
                  <div className="md:col-span-2">
                    <p className="overline mb-2">Financing inquiry</p>
                    <h3 className="heading-3">Tell us about your project.</h3>
                  </div>
                  <div>
                    <label className="label-base">Full Name</label>
                    <input data-testid="financing-name" required className="input-base" value={form.full_name} onChange={onChange("full_name")} />
                  </div>
                  <div>
                    <label className="label-base">Phone</label>
                    <input data-testid="financing-phone" required className="input-base" value={form.phone} onChange={onChange("phone")} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-base">Email</label>
                    <input data-testid="financing-email" required type="email" className="input-base" value={form.email} onChange={onChange("email")} />
                  </div>
                  <div>
                    <label className="label-base">Project Type</label>
                    <select data-testid="financing-project" className="input-base" value={form.project_type} onChange={onChange("project_type")}>
                      <option value="">Select…</option>
                      <option>ADU Construction</option>
                      <option>HVAC</option>
                      <option>Plumbing</option>
                      <option>Remodeling</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-base">Estimated Budget</label>
                    <select data-testid="financing-budget" className="input-base" value={form.estimated_budget} onChange={onChange("estimated_budget")}>
                      <option value="">Select…</option>
                      <option>Under $10k</option>
                      <option>$10k–$25k</option>
                      <option>$25k–$75k</option>
                      <option>$75k–$150k</option>
                      <option>$150k+</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-base">Message</label>
                    <textarea data-testid="financing-message" rows={4} className="input-base resize-none" value={form.message} onChange={onChange("message")} />
                  </div>
                  <div className="md:col-span-2">
                    <button data-testid="financing-submit" disabled={submitting} className="btn-primary w-full sm:w-auto">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Submit Financing Inquiry
                    </button>
                    <p className="text-xs text-brand-ash mt-3">Approval subject to lender criteria. We'll discuss options after we understand your project.</p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
